import { Button, Image, Stack, Text, Title } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { IconCircleArrowUp } from '@tabler/icons-react';
import { tKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { usePostHog } from 'posthog-js/react';
import { useEffect } from 'react';
import ReactPlayer from 'react-player';
import { useHistory, useRouteMatch } from 'react-router';
import { captureEvent } from 'shared/analytics/capture-event';
import { useSendUpsellRequest } from 'shared/hooks/use-send-slack-notification';
import { useBuildUpsellFeature } from '../hooks';
import { UpsellFeatureSchema } from '../types';

export const Upsell = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const posthog = usePostHog();
  const { params } = useRouteMatch<{ featureName: string }>();

  const getFeatureDefinition = useBuildUpsellFeature();
  const { sendUpsellRequest } = useSendUpsellRequest();

  const upsellFeatureParse = UpsellFeatureSchema.safeParse(params.featureName);

  const [hasRequestedUpgrade, setHasRequestedUpgrade] = useLocalStorage<boolean>({
    key: upsellFeatureParse.success ? `upsell_request_${upsellFeatureParse.data}` : '',
    defaultValue: false,
  });

  useEffect(() => {
    if (!upsellFeatureParse.success) history.push('/dashboard');
    else
      captureEvent(posthog, 'upsell:page_viewed', {
        feature_name: upsellFeatureParse.data,
      });
  }, [upsellFeatureParse.success, upsellFeatureParse.data, history, posthog]);

  if (!upsellFeatureParse.success) return null;

  const featureName = upsellFeatureParse.data;

  const handleUpsell = () => {
    captureEvent(posthog, 'upsell:upgrade_clicked', {
      feature_name: featureName,
    });
    window.Intercom('showNewMessage', t(tKeys.UPSELL.INTERCOM_MESSAGE, { feature: featureName }));
    sendUpsellRequest(featureName);
    setHasRequestedUpgrade(true);
  };

  const feature = getFeatureDefinition(featureName);

  return (
    <Stack p="xl" h="100vh" gap="sm" align="center">
      <Title fw={700} c="purple.7" order={2}>
        {feature.title}
      </Title>
      <Text ta="center">{feature.description}</Text>
      <Button
        onClick={handleUpsell}
        rightSection={<IconCircleArrowUp stroke={1.5} />}
        mb="md"
        disabled={hasRequestedUpgrade}
      >
        {t(tKeys.UPSELL.UPGRADE)}
      </Button>
      {feature.mediaSrc?.endsWith('.mp4') ? (
        <ReactPlayer
          style={{ borderRadius: '8px' }}
          src={feature.mediaSrc}
          controls
          width="80%"
          height="auto"
        />
      ) : (
        <Image src={feature.mediaSrc} alt={feature.title} radius="md" maw="80%" h="auto" />
      )}
    </Stack>
  );
};
